import React from 'react';
import { CheckCircle2, Copy, Download, Globe2, Loader2, RefreshCw, Save, AlertTriangle, FileCheck } from 'lucide-react';
import { getComparableMetadataValue, SHARED_FIELD_LABELS } from './linkedLanguageUtils';

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

const formatFieldLabel = (field) => {
  if (SHARED_FIELD_LABELS[field]) return SHARED_FIELD_LABELS[field];
  const fallback = String(field).split('.').pop() || String(field);
  return fallback
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatTranslationIssueDetail = (detail) => {
  if (!detail) return '';

  if (detail.type === 'metadata') {
    return `Guide metadata: ${formatFieldLabel(detail.field)}`;
  }

  if (detail.type === 'poi') {
    return `Stop ${detail.sequence} (${detail.sourceName}): ${formatFieldLabel(detail.field)}`;
  }

  return formatFieldLabel(detail.field || '');
};

const TranslationIssueList = ({ details = [], emptyLabel = '' }) => {
  if (details.length === 0) return emptyLabel ? <p className="mt-2 text-xs text-slate-600">{emptyLabel}</p> : null;

  const visibleDetails = details.slice(0, 8);

  return (
    <div className="mt-2">
      <ul className="space-y-1 text-xs text-slate-700">
        {visibleDetails.map((detail, index) => (
          <li key={`${detail.type || 'field'}-${detail.field || 'unknown'}-${detail.sequence || 'guide'}-${index}`}>
            {formatTranslationIssueDetail(detail)}
          </li>
        ))}
      </ul>
      {details.length > visibleDetails.length && (
        <p className="mt-2 text-xs text-slate-600">
          Showing {visibleDetails.length} of {details.length} fields.
        </p>
      )}
    </div>
  );
};

const triggerJsonDownload = (guideData, fallbackName = 'guide') => {
  const filename = guideData?.metadata?.filename || `${fallbackName}.json`;
  const blob = new Blob([`${JSON.stringify(guideData, null, 2)}\n`], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const LinkedLanguageManager = ({
  currentGuide,
  linkedGuides = [],
  selectedGuideIds = [],
  loading = false,
  saving = false,
  error = '',
  onRefresh = () => {},
  onToggleGuide = () => {},
  onSelectAll = () => {},
  onSyncSelected = () => {},
  onSaveSelected = () => {},
  onMarkTranslationReviewed = () => {},
}) => {
  const selectedCount = selectedGuideIds.length;
  const loadedCount = linkedGuides.length;
  const currentLanguage = currentGuide?.metadata?.language || 'unknown';
  const selectedGuides = linkedGuides.filter((guide) => {
    const guideId = String(guide?.catalog?.ID || guide?.guideData?.metadata?.id || '');
    return selectedGuideIds.includes(guideId);
  });

  const handleDownloadSelected = () => {
    selectedGuides.forEach((guide) => {
      const fallbackName = String(guide?.guideData?.metadata?.id || guide?.catalog?.ID || 'guide');
      triggerJsonDownload(guide?.guideData, fallbackName);
    });
  };

  return (
    <details className="rounded-lg border border-slate-200 bg-slate-50 shadow-sm">
      <summary className="cursor-pointer list-none select-none px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-slate-900">
            <Globe2 className="h-5 w-5 text-sky-600" />
            <div>
              <h3 className="text-lg font-semibold">Linked Languages</h3>
              <p className="text-sm text-slate-600">
                Compare linked language versions, apply shared data updates, and confirm reviewed translations.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="rounded-full bg-white px-2.5 py-1 font-medium text-slate-700">
              {loadedCount} loaded
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 font-medium text-slate-700">
              {selectedCount} selected
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 font-medium text-slate-700">
              Current: {currentLanguage}
            </span>
          </div>
        </div>
      </summary>

      <div className="px-5 pb-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="mt-1 text-sm text-slate-600">
            Compare the current guide against its linked language variants, then sync shared fields without changing the public JSON format.
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Current language: <span className="font-medium text-slate-700">{currentLanguage}</span>
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Workflow: <span className="font-medium text-slate-700">1.</span> Refresh linked guides
            {' '}→ <span className="font-medium text-slate-700">2.</span> Select the ones to update
            {' '}→ <span className="font-medium text-slate-700">3.</span> Apply shared data
            {' '}→ <span className="font-medium text-slate-700">4.</span> Save synced guides.
          </p>
        </div>

        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              title="Fetch the latest linked-language guides and recompute shared-field and translation-review differences."
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : '1. Refresh linked guides'}
            </button>
            <button
              type="button"
              onClick={onSyncSelected}
              disabled={saving || selectedCount === 0}
              title={selectedCount === 0
                ? 'Select at least one linked guide first.'
                : 'Copy shared non-text fields from the current guide into the selected linked guides in memory. Nothing is saved until you click Save synced guides.'}
              className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Copy className="h-4 w-4" />
              3. Apply shared data
            </button>
            <button
              type="button"
              onClick={onSaveSelected}
              disabled={saving || selectedCount === 0}
              title={selectedCount === 0
                ? 'Select at least one linked guide first.'
                : 'Persist the selected linked guides to storage, including any shared-field changes you already applied.'}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving...' : '4. Save synced guides'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleDownloadSelected}
              disabled={selectedCount === 0}
              title={selectedCount === 0
                ? 'Select at least one linked guide first.'
                : 'Download the selected linked-guide JSON files for inspection or backup. This does not save or modify anything.'}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download className="h-3.5 w-3.5" />
              Export selected JSONs
            </button>
            <button
              type="button"
              onClick={onMarkTranslationReviewed}
              disabled={saving || selectedCount === 0}
              title={selectedCount === 0
                ? 'Select at least one linked guide first.'
                : 'After manually checking the translated text, store the current source text as the review baseline for the selected guides. This updates review status only and does not sync content.'}
              className="inline-flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-xs font-medium text-violet-700 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FileCheck className="h-3.5 w-3.5" />
              Confirm text reviewed
            </button>
          </div>
        </div>
      </div>

      <details className="mt-4 rounded-lg border border-sky-200 bg-sky-50">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-sky-800">
          How does this work? What gets synchronized?
        </summary>
        <div className="px-4 pb-4 text-sm text-sky-900 space-y-2">
          <p>
            Guides translated from the same parent guide share <strong>language-independent data</strong>:
            stop coordinates, categories, tiers, photo URLs, opening hours, related guides, and
            guide-level settings like the thumbnail and configuration flags.
          </p>
          <p>
            <strong>"Apply shared data"</strong> copies those values <strong>from the guide you currently
            have open into the selected linked guides</strong>. Translated text (titles, descriptions,
            stories) is never touched by sync.
          </p>
          <p>
            Translated <strong>text</strong> is checked separately: the violet badges warn when a linked
            guide has empty text fields, text identical to the source language, or when the source text
            changed after the last review. Once you have reviewed the translated text, click
            <strong> "Confirm text reviewed"</strong> to store the current source text as the new baseline.
          </p>
          <p>
            Syncing only updates the guides in memory — nothing is written until you click
            <strong> "Save synced guides"</strong>.
          </p>
          <p className="rounded border border-sky-200 bg-white px-3 py-2">
            <strong>Why does "Source text changed" stay after I sync and save?</strong>{' '}
            Sync and save only handle the non-text shared data — they never update the text-review
            baseline. That warning means the current guide's <em>text</em> was edited after the
            translation was last reviewed. To clear it: check that the translated text is still
            correct, select the guide, and click <strong>"Confirm text reviewed"</strong>.
          </p>
        </div>
      </details>

      {error && (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-600">
        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-700">
          2. Select guides to update
        </span>
        <span>{loadedCount} linked guide{loadedCount === 1 ? '' : 's'} loaded</span>
        <span>{selectedCount} selected</span>
        {loadedCount > 0 && (
          <button
            type="button"
            onClick={() => onSelectAll(selectedCount !== loadedCount)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            {selectedCount === loadedCount ? 'Clear selection' : 'Select all'}
          </button>
        )}
      </div>

      {loadedCount === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600">
          Load a published guide with a `parent_guide_id` group to compare and update linked languages here.
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {linkedGuides.map((guide) => {
            const { catalog, guideData, comparison } = guide;
            const guideId = String(catalog?.ID || guideData?.metadata?.id || '');
            const checked = selectedGuideIds.includes(guideId);
            const diffCount = comparison?.totalDiffs || 0;
            const pointDiffs = comparison?.pointDiffs || [];
            const metadataDiffs = comparison?.metadataDiffs || [];
            const translationComparison = guide.translationComparison || {};
            const translationNeedsReview = Boolean(translationComparison.needsReview);
            const reviewBaselineMissing = Boolean(translationComparison.reviewBaselineMissing);
            const sourceChangedSinceReview = Boolean(translationComparison.sourceChangedSinceReview);
            const translationIssueCount = translationComparison.totalIssues || 0;

            return (
              <div key={guideId} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={checked}
                      onChange={() => onToggleGuide(guideId)}
                    />
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-base font-semibold text-slate-900">
                          {guideData?.metadata?.name || catalog?.Name || 'Untitled guide'}
                        </h4>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                          {guideData?.metadata?.language || catalog?.Language || 'unknown'}
                        </span>
                        {diffCount === 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Shared fields in sync
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {diffCount} shared field{diffCount === 1 ? '' : 's'} differ{diffCount === 1 ? 's' : ''} from current guide
                          </span>
                        )}
                        {translationNeedsReview ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {reviewBaselineMissing
                              ? 'Translation not yet confirmed'
                              : sourceChangedSinceReview
                                ? 'Source text changed'
                                : `${translationIssueCount} text issue${translationIssueCount === 1 ? '' : 's'}`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Text review current
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-slate-600">
                        Database ID {catalog?.ID || guideData?.metadata?.id || '—'} • Parent guide {guideData?.metadata?.parent_guide_id || '—'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => triggerJsonDownload(guideData, guideId || 'guide')}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    <Download className="h-4 w-4" />
                    Download JSON
                  </button>
                </div>

                <div className="mt-3">

                {metadataDiffs.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-slate-800">Metadata differences</h5>
                    <div className="mt-2 overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead>
                          <tr className="text-left text-slate-500">
                            <th className="py-2 pr-4">Field</th>
                            <th className="py-2 pr-4">Current guide</th>
                            <th className="py-2 pr-4">Linked guide</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {metadataDiffs.map((field) => (
                            <tr key={field}>
                              <td className="py-2 pr-4 font-medium text-slate-700">{formatFieldLabel(field)}</td>
                              <td className="py-2 pr-4 text-slate-600">{formatValue(getComparableMetadataValue(currentGuide, field))}</td>
                              <td className="py-2 pr-4 text-slate-600">{formatValue(getComparableMetadataValue(guideData, field))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <h5 className="text-sm font-medium text-slate-800">POI shared-field differences</h5>
                  {pointDiffs.length === 0 ? (
                    <p className="mt-2 text-sm text-emerald-700">All compared POI shared fields match the current guide.</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {pointDiffs.slice(0, 8).map((pointDiff) => (
                        <div key={`${guideId}-${pointDiff.sequence}`} className="rounded-lg border border-yellow-400 bg-yellow-200 px-3 py-2 text-sm shadow-sm">
                          <div className="font-medium text-yellow-950">
                            Stop {pointDiff.sequence}: {pointDiff.sourceName}
                          </div>
                          <div className="mt-1 text-yellow-900">
                            {pointDiff.missing
                              ? 'Missing from linked guide'
                              : pointDiff.fields.map((field) => formatFieldLabel(field)).join(', ')}
                          </div>
                        </div>
                      ))}
                      {pointDiffs.length > 8 && (
                        <p className="text-xs text-slate-500">
                          Showing 8 of {pointDiffs.length} POI diff entries.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <h5 className="text-sm font-medium text-slate-800">Translation review status</h5>
                  {translationNeedsReview ? (
                    <div className="mt-2 space-y-2 text-sm text-slate-600">
                      {reviewBaselineMissing && (
                        <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-violet-800">
                          This translation has not been confirmed against the current source text yet.
                          If the translated text looks correct, select this guide and click <strong>Confirm text reviewed</strong>.
                          This does not change the translation. It only records that you checked it.
                        </div>
                      )}
                      {sourceChangedSinceReview && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                          The current guide text changed after this translation was last marked reviewed.
                          <span className="mt-1 block text-xs text-amber-700">
                            Syncing shared fields does not clear this — it only copies non-text data.
                            Re-check the translated text, then select this guide and click
                            <strong> "Confirm text reviewed"</strong> to update the baseline.
                          </span>
                        </div>
                      )}
                      {translationComparison.missingPointCount > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                          {translationComparison.missingPointCount} stop{translationComparison.missingPointCount === 1 ? '' : 's'} missing from the linked translation.
                        </div>
                      )}
                      {translationComparison.missingFieldCount > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                          {translationComparison.missingFieldCount} translated text field{translationComparison.missingFieldCount === 1 ? '' : 's'} still empty.
                          <TranslationIssueList details={translationComparison.missingFieldDetails} />
                        </div>
                      )}
                      {translationComparison.sameAsSourceCount > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                          {translationComparison.sameAsSourceCount} text field{translationComparison.sameAsSourceCount === 1 ? '' : 's'} still match the source language exactly.
                          <TranslationIssueList details={translationComparison.sameAsSourceDetails} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-emerald-700">This linked guide is marked reviewed against the current source text.</p>
                  )}
                </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </details>
  );
};

export default LinkedLanguageManager;
